import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ANALYTICS_CONFIG, PERIOD_OPTIONS, resolvePeriodRange } from '../../../config/analytics-config.js';
import { mapAnalyticsSecurityError } from '../../../services/firebase-auth.js';
import { useOriginTagsCatalog } from '../../../shared/hooks/useOriginTagsCatalog.js';
import { loadLeadsAnalytics, syncLeadsFromSource } from '../services/leads-service.js';
import { cacheGet, cacheInvalidate, cacheSet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';

const LeadsDataContext = createContext(null);

const PERIOD_OPTIONS_WITH_CUSTOM = PERIOD_OPTIONS.concat([{ key: 'custom', label: 'Personalizado' }]);

function leadsCacheKey(period, dateFrom, dateTo, originTags) {
  let range;
  if (period === 'custom' && dateFrom && dateTo) {
    range = { dateFrom, dateTo };
  } else {
    range = resolvePeriodRange(period);
  }
  const tags = Array.isArray(originTags) ? originTags.slice().sort().join(',') : '';
  return 'leads:' + range.dateFrom + '|' + range.dateTo + '|ot:' + tags;
}

function resolveLeadsRange(period, dateFrom, dateTo) {
  if (period === 'custom' && dateFrom && dateTo) {
    return { dateFrom, dateTo };
  }
  return resolvePeriodRange(period === 'custom' ? ANALYTICS_CONFIG.defaultPeriod : period);
}

/** Período / fechas recordados al salir/volver a Leads. */
let rememberedPeriod = ANALYTICS_CONFIG.defaultPeriod;
let rememberedDateFrom = '';
let rememberedDateTo = '';

export function LeadsDataProvider({ children, email }) {
  const [period, setPeriodState] = useState(rememberedPeriod);
  const [dateFrom, setDateFromState] = useState(rememberedDateFrom);
  const [dateTo, setDateToState] = useState(rememberedDateTo);
  const [sellerSort, setSellerSort] = useState('criticity');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [metaBar, setMetaBar] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(false);
  const [active, setActive] = useState(false);
  const sellersCache = useRef([]);
  const attentionCache = useRef(null);
  const loadSeq = useRef(0);
  const hasSearched = useRef(false);

  const originCatalog = useOriginTagsCatalog({ persistKey: 'leads' });

  const setPeriod = useCallback((p) => {
    rememberedPeriod = p;
    setPeriodState(p);
    if (p !== 'custom') {
      rememberedDateFrom = '';
      rememberedDateTo = '';
      setDateFromState('');
      setDateToState('');
    }
  }, []);

  const setDateFrom = useCallback((v) => {
    rememberedDateFrom = v;
    setDateFromState(v);
  }, []);

  const setDateTo = useCallback((v) => {
    rememberedDateTo = v;
    setDateToState(v);
  }, []);

  const applyPayload = useCallback((payload) => {
    sellersCache.current = (payload && payload.sellers) || [];
    attentionCache.current = payload && payload.attention;
    setData(payload);
    const coverage =
      (payload && payload.stageHistoryCoverage) ||
      (payload && payload.dataQuality && payload.dataQuality.stageHistoryCoverage);
    const sync = payload && payload.kommoSync;
    const parts = [];
    if (coverage && coverage.label) parts.push(coverage.label);
    if (sync && sync.message) parts.push(sync.message);
    if (payload && payload.generatedAt) parts.push('Generado ' + payload.generatedAt);
    if (payload && payload._fromCache) parts.push('caché');
    setMetaBar(parts.join(' · '));
  }, []);

  const originTags = originCatalog.selected;
  const originReady = originCatalog.ready;
  const range = useMemo(
    () => resolveLeadsRange(period, dateFrom, dateTo),
    [period, dateFrom, dateTo]
  );
  const periodLabel = useMemo(() => {
    if (period === 'all') return 'Completo';
    if (range && range.dateFrom && range.dateTo) return range.dateFrom + ' → ' + range.dateTo;
    return 'período';
  }, [period, range]);
  const customReady = period !== 'custom' || (!!dateFrom && !!dateTo);

  const load = useCallback(
    async (opts) => {
      if (!originReady || !customReady) return;
      const force = !!(opts && opts.force);
      const tags = Array.isArray(originTags) ? originTags : [];
      const key = leadsCacheKey(period, dateFrom, dateTo, tags);
      const seq = ++loadSeq.current;
      hasSearched.current = true;
      if (!force) {
        const hit = cacheGet(key);
        if (hit) {
          if (seq !== loadSeq.current) return;
          applyPayload({ ...hit.data, _fromCache: true });
          setLoading(false);
          setError('');
          return;
        }
      } else {
        cacheInvalidate(key);
      }

      setLoading(true);
      setError('');
      try {
        const r = resolveLeadsRange(period, dateFrom, dateTo);
        const payload = await loadLeadsAnalytics({
          dateFrom: r.dateFrom,
          dateTo: r.dateTo,
          sellerId: '',
          pipelineId: '',
          originTags: tags
        });
        if (seq !== loadSeq.current) return;
        cacheSet(key, payload);
        applyPayload({ ...payload, _fromCache: false });
      } catch (err) {
        if (seq !== loadSeq.current) return;
        console.error('[leads] load', err);
        setError(mapAnalyticsSecurityError(err) || (err && err.message) || 'Error al cargar.');
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [period, dateFrom, dateTo, originTags, originReady, customReady, applyPayload]
  );

  const ensureLoaded = useCallback(() => {
    setActive(true);
  }, []);

  useAnalyticsRefresh(
    useCallback(() => {
      if (active && hasSearched.current) load({ force: true });
    }, [active, load])
  );

  const runSync = useCallback(async () => {
    setSyncBusy(true);
    setError('');
    setMetaBar(
      'Sync full en curso (leads + notas + Seguimiento). Puede tardar 2–6 min; no cierres la pestaña.'
    );
    try {
      const res = await syncLeadsFromSource({ tipo: 'full' });
      if (!res || res.ok === false || res.success === false) {
        throw new Error((res && (res.error || res.message)) || 'Sync falló.');
      }
      cacheInvalidate(leadsCacheKey(period, dateFrom, dateTo, originTags));
      await load({ force: true });
    } catch (err) {
      console.error('[leads] sync', err);
      setError(mapAnalyticsSecurityError(err) || (err && err.message) || 'Error al sincronizar.');
    } finally {
      setSyncBusy(false);
    }
  }, [load, period, dateFrom, dateTo, originTags]);

  const reload = useCallback(() => {
    load({ force: true });
  }, [load]);

  const cancelSearch = useCallback(() => {
    loadSeq.current += 1;
    setLoading(false);
  }, []);

  const value = useMemo(
    () => ({
      email,
      period,
      setPeriod,
      periodOptions: PERIOD_OPTIONS_WITH_CUSTOM,
      dateFrom,
      setDateFrom,
      dateTo,
      setDateTo,
      range,
      periodLabel,
      customReady,
      sellerSort,
      setSellerSort,
      // No mezclar «faltan fechas custom» ni catálogo de orígenes con loading de búsqueda.
      loading,
      awaitingOrigins: !originReady,
      awaitingCustomDates: period === 'custom' && !customReady,
      error,
      setError,
      data,
      metaBar,
      syncBusy,
      attentionExpanded,
      setAttentionExpanded,
      runSync,
      reload,
      cancelSearch,
      ensureLoaded,
      sellersCache,
      attentionCache,
      originTags,
      setOriginTags: originCatalog.setSelected,
      originTagOptions: originCatalog.options,
      originTagsLoading: originCatalog.loading,
      selectOriginMetaOnly: originCatalog.selectMetaOnly,
      selectOriginAll: originCatalog.selectAll,
      clearOriginTags: originCatalog.clearAll
    }),
    [
      email,
      period,
      setPeriod,
      dateFrom,
      setDateFrom,
      dateTo,
      setDateTo,
      range,
      periodLabel,
      customReady,
      sellerSort,
      loading,
      originReady,
      error,
      data,
      metaBar,
      syncBusy,
      attentionExpanded,
      runSync,
      reload,
      cancelSearch,
      ensureLoaded,
      originTags,
      originCatalog.setSelected,
      originCatalog.options,
      originCatalog.loading,
      originCatalog.selectMetaOnly,
      originCatalog.selectAll,
      originCatalog.clearAll
    ]
  );

  return <LeadsDataContext.Provider value={value}>{children}</LeadsDataContext.Provider>;
}

export function useLeadsData() {
  const ctx = useContext(LeadsDataContext);
  if (!ctx) throw new Error('useLeadsData debe usarse dentro de LeadsDataProvider');
  return ctx;
}
